import { useMemo } from 'react';
import { useAtomValue } from 'jotai/utils';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { useAiAgentAvailable } from 'src/hooks/useAiAgentAvailable';
import { useBudgetAllowed } from 'src/chat/BudgetIndicator';
import { useDeliveryInsightsEnabled } from 'src/hooks/useDeliveryInsightsEnabled';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';
import { useRemoveBackgroundAvailable } from 'src/hooks/useRemoveBackgroundAvailable';
import useRoleManager from 'src/hooks/useRoleManager';
import type { StarterGateContext } from './starterGates';

export type StarterGates = {
  /** The agent is usable and the account has credits. The chat proxy
   *  pre-flights the budget and fails the turn outright, so a chip at zero
   *  credits would die on click. */
  agentReady: boolean;
  /** Addon settings have hydrated. Until then every addon-gated chip would
   *  flash in and out on each page load. */
  hydrated: boolean;
  ctx: StarterGateContext;
};

/**
 * Assembles everything the starters' `requires` tags need, in one place, so
 * `starterGates.ts` stays hook-free and testable. Shared by the nudge rows and
 * the chat empty state.
 */
export function useStarterGates(): StarterGates {
  const addonsSettings = useAtomValue(addonsSettingsAtom);
  const { inAppAvailable } = useAiAgentAvailable();
  const budgetAllowed = useBudgetAllowed();
  const { isUser } = useRoleManager();
  const removeBackgroundAvailable = useRemoveBackgroundAvailable();
  const printExportAvailable = usePrintExportAvailable();
  const campaignLinksAvailable = useDeliveryInsightsEnabled();

  // Only needed to decide whether metadata chips can work at all; cheap and
  // shared with the schema settings pages via react-query.
  const { data: schemas } = useAssetSchemasListQuery('ACTIVE');
  const hasActiveSchema = Array.isArray(schemas?.data)
    ? schemas.data.length > 0
    : Array.isArray(schemas)
    ? schemas.length > 0
    : false;

  const ctx = useMemo<StarterGateContext>(
    () => ({
      isUser,
      hasActiveSchema,
      removeBackgroundAvailable,
      printExportAvailable,
      campaignLinksAvailable,
    }),
    [isUser, hasActiveSchema, removeBackgroundAvailable, printExportAvailable, campaignLinksAvailable],
  );

  return {
    agentReady: inAppAvailable && budgetAllowed,
    hydrated: addonsSettings !== undefined,
    ctx,
  };
}

export default useStarterGates;
